import React, { useState, useMemo, useEffect, useCallback } from "react";
import {
  Alert,
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Stack,
  Grid,
  Tooltip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  CircularProgress,
} from "@mui/material";
import { motion } from "framer-motion";

import PendingActionsIcon from "@mui/icons-material/PendingActions";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import StorefrontIcon from "@mui/icons-material/Storefront";
import VisibilityIcon from "@mui/icons-material/Visibility";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import CancelIcon from "@mui/icons-material/Cancel";
import CloseIcon from "@mui/icons-material/Close";
import LoadingSpinner from "../components/LoadingSpinner";
import { approveGroupBusiness, fetchGroupBusinesses } from "../services/groupBusinessService";
import formatDate from "../shared/formatDate";

const brand = "#800b33";
const brandHover = "#6c0a2b";

const tzs = (n) => new Intl.NumberFormat("en-TZ").format(n);

const fadeIn = {
  hidden: { opacity: 0, y: 18 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.08, duration: 0.4, ease: "easeOut" },
  }),
};

const STATUS_COPY = {
  ACTIVE: "Active",
  PENDING_APPROVAL: "Pending approval",
  REJECTED: "Rejected",
};

export default function AdminGroupBusinessApprovals() {
  const [search, setSearch] = useState("");
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [openDetailsDialog, setOpenDetailsDialog] = useState(false);

  const [decisionDialog, setDecisionDialog] = useState({
    open: false,
    type: null,
    business: null,
    rejectionReason: "",
    submitting: false,
  });

  const [toast, setToast] = useState({
    open: false,
    severity: "success",
    message: "",
  });

  const showToast = (severity, message) => {
    setToast({ open: true, severity, message });
  };

  const loadBusinesses = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await fetchGroupBusinesses();
      setBusinesses(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message || "Failed to load businesses.");
      showToast("error", err.message || "Failed to load businesses.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadBusinesses();
  }, [loadBusinesses]);

  const filtered = useMemo(
    () =>
      businesses.filter((b) => {
        const term = search.toLowerCase();
        return (
          b.name?.toLowerCase().includes(term) ||
          b.group?.toLowerCase().includes(term) ||
          b.group_name?.toLowerCase().includes(term)
        );
      }),
    [businesses, search]
  );

  const summary = useMemo(() => {
    const pending = businesses.filter((b) => b.status === "PENDING_APPROVAL").length;
    const approved = businesses.filter((b) => b.status === "ACTIVE").length;
    const total = businesses.length;
    return { pending, approved, total };
  }, [businesses]);

  const getStatusColor = (status) => {
    if (status === "ACTIVE") return "success";
    if (status === "PENDING_APPROVAL") return "warning";
    if (status === "REJECTED") return "error";
    return "default";
  };

  const handleOpenDetails = (business) => {
    setSelectedBusiness(business);
    setOpenDetailsDialog(true);
  };

  const handleCloseDetails = () => {
    setOpenDetailsDialog(false);
    setSelectedBusiness(null);
  };

  const openDecisionDialog = (business, type) => {
    setOpenDetailsDialog(false);
    setDecisionDialog({
      open: true,
      type,
      business,
      rejectionReason: "",
      submitting: false,
    });
  };

  const closeDecisionDialog = () => {
    if (decisionDialog.submitting) return;
    setDecisionDialog({
      open: false,
      type: null,
      business: null,
      rejectionReason: "",
      submitting: false,
    });
  };

  const handleDecisionReasonChange = (event) => {
    const value = event.target.value;
    setDecisionDialog((current) => ({ ...current, rejectionReason: value }));
  };

  const handleConfirmDecision = async () => {
    const { business, type, rejectionReason } = decisionDialog;
    if (!business || !type) return;
    if (type === "reject" && !rejectionReason.trim()) {
      showToast("warning", "A rejection reason is required before continuing.");
      return;
    }

    try {
      setDecisionDialog((current) => ({ ...current, submitting: true }));
      await approveGroupBusiness(
        business.id,
        type === "approve",
        type === "reject" ? rejectionReason.trim() : undefined
      );
      closeDecisionDialog();
      showToast(
        "success",
        type === "approve"
          ? `"${business.name}" is now active for ${business.group_name}.`
          : `"${business.name}" has been rejected and recorded.`
      );
      await loadBusinesses();
    } catch (err) {
      setDecisionDialog((current) => ({ ...current, submitting: false }));
      showToast("error", err.message || "The action could not be completed.");
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return <Typography color="error" align="center" sx={{ my: 4 }}>Error: {error}</Typography>;
  }

  const decisionBusiness = decisionDialog.business;
  const isRejectFlow = decisionDialog.type === "reject";

  return (
    <Box sx={{ p: 4 }}>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 900, color: brand }}>
          Group Business Approvals
        </Typography>
        <Typography variant="subtitle2" color="text.secondary">
          Review and activate new business ventures proposed by groups.
        </Typography>
      </Box>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <StatCard
            icon={<PendingActionsIcon sx={{ color: brand }} />}
            label="Pending Approvals"
            value={summary.pending}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <StatCard
            icon={<CheckCircleOutlineIcon sx={{ color: brand }} />}
            label="Total Approved"
            value={summary.approved}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <StatCard
            icon={<StorefrontIcon sx={{ color: brand }} />}
            label="Total Businesses"
            value={summary.total}
          />
        </Grid>
      </Grid>

      <motion.div variants={fadeIn} initial="hidden" animate="visible" custom={1}>
        <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
          <CardContent>
            <TextField
              fullWidth
              placeholder="Search by business name or group..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{ mb: 2 }}
            />
            <TableContainer component={Paper} elevation={0}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Business Name</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Group Name</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Capital</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 700 }}>
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filtered.map((b) => (
                    <TableRow key={b.id} hover>
                      <TableCell>{b.name}</TableCell>
                      <TableCell>{b.group_name}</TableCell>
                      <TableCell>{tzs(b.capital)} TZS</TableCell>
                      <TableCell>
                        <Chip
                          label={STATUS_COPY[b.status] || b.status}
                          color={getStatusColor(b.status)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Stack direction="row" spacing={0.5} justifyContent="center">
                          <Tooltip title="View details">
                            <IconButton onClick={() => handleOpenDetails(b)} size="small">
                              <VisibilityIcon fontSize="inherit" />
                            </IconButton>
                          </Tooltip>
                          {b.status === "PENDING_APPROVAL" && (
                            <>
                              <Tooltip title="Approve business">
                                <IconButton onClick={() => openDecisionDialog(b, "approve")} color="success" size="small">
                                  <TaskAltIcon fontSize="inherit" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Reject business">
                                <IconButton onClick={() => openDecisionDialog(b, "reject")} color="error" size="small">
                                  <CancelIcon fontSize="inherit" />
                                </IconButton>
                              </Tooltip>
                            </>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </motion.div>

      <Dialog open={openDetailsDialog} onClose={handleCloseDetails} fullWidth maxWidth="sm">
        {selectedBusiness && (
          <>
            <DialogTitle>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                {selectedBusiness.name}
                <IconButton onClick={handleCloseDetails}><CloseIcon /></IconButton>
              </Stack>
            </DialogTitle>
            <DialogContent dividers>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {selectedBusiness.description || "No description provided."}
              </Typography>
              <Stack spacing={1}>
                <Typography><strong>Group:</strong> {selectedBusiness.group_name}</Typography>
                <Typography><strong>Capital:</strong> {tzs(selectedBusiness.capital)} TZS</Typography>
                <Typography><strong>Type:</strong> {selectedBusiness.business_type}</Typography>
                <Typography><strong>Initiated By:</strong> {selectedBusiness.created_by_name}</Typography>
                <Typography><strong>Date:</strong> {formatDate(selectedBusiness.created_at)}</Typography>
              </Stack>
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
              {selectedBusiness.status === "PENDING_APPROVAL" ? (
                <>
                  <Button variant="outlined" color="error" onClick={() => openDecisionDialog(selectedBusiness, "reject")}>
                    Reject
                  </Button>
                  <Button variant="contained" sx={{ bgcolor: brand, "&:hover": { bgcolor: brandHover } }} onClick={() => openDecisionDialog(selectedBusiness, "approve")}>
                    Approve
                  </Button>
                </>
              ) : (
                <Button onClick={handleCloseDetails}>Close</Button>
              )}
            </DialogActions>
          </>
        )}
      </Dialog>

      <Dialog open={decisionDialog.open} onClose={closeDecisionDialog} fullWidth maxWidth="sm">
        <DialogTitle sx={{ pb: 1 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 800 }}>
                {isRejectFlow ? "Reject business application" : "Approve business activation"}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {isRejectFlow
                  ? "Record a clear decision and keep an audit-friendly rejection reason."
                  : "Confirm activation after reviewing the submitted business details below."}
              </Typography>
            </Box>
            <IconButton onClick={closeDecisionDialog} disabled={decisionDialog.submitting}>
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          {decisionBusiness && (
            <Stack spacing={2.5}>
              <Alert severity={isRejectFlow ? "warning" : "info"} sx={{ borderRadius: 2 }}>
                {isRejectFlow
                  ? `The business will remain inactive and the rejection reason will be stored for compliance review.`
                  : `Once approved, ${decisionBusiness.name} becomes active and available to ${decisionBusiness.group_name}.`}
              </Alert>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <InfoTile label="Business Name" value={decisionBusiness.name} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <InfoTile label="Group" value={decisionBusiness.group_name} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <InfoTile label="Capital" value={`${tzs(decisionBusiness.capital)} TZS`} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <InfoTile label="Initiated By" value={decisionBusiness.created_by_name || "-"} />
                </Grid>
                <Grid item xs={12}>
                  <InfoTile label="Description" value={decisionBusiness.description || "No description provided."} multiline />
                </Grid>
              </Grid>

              {isRejectFlow && (
                <TextField
                  fullWidth
                  label="Rejection reason"
                  placeholder="State the compliance or business reason for rejecting this application"
                  value={decisionDialog.rejectionReason}
                  onChange={handleDecisionReasonChange}
                  required
                  multiline
                  minRows={3}
                  autoFocus
                />
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, justifyContent: "space-between" }}>
          <Button onClick={closeDecisionDialog} disabled={decisionDialog.submitting} sx={{ color: "text.secondary" }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color={isRejectFlow ? "error" : "primary"}
            onClick={handleConfirmDecision}
            disabled={decisionDialog.submitting || (isRejectFlow && !decisionDialog.rejectionReason.trim())}
            sx={!isRejectFlow ? { bgcolor: brand, "&:hover": { bgcolor: brandHover } } : undefined}
            startIcon={decisionDialog.submitting ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {decisionDialog.submitting
              ? (isRejectFlow ? "Rejecting..." : "Approving...")
              : (isRejectFlow ? "Confirm rejection" : "Confirm approval")}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={() => setToast((current) => ({ ...current, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          elevation={6}
          variant="filled"
          onClose={() => setToast((current) => ({ ...current, open: false }))}
          severity={toast.severity}
          sx={{ width: "100%" }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

const StatCard = ({ icon, label, value }) => (
  <Card sx={{ p: 2, borderRadius: 3, boxShadow: 3 }}>
    <Stack direction="row" alignItems="center" spacing={2}>
      <Box sx={{ width: 52, height: 52, borderRadius: "50%", background: `${brand}1A`, display: "grid", placeItems: "center" }}>
        {icon}
      </Box>
      <Box>
        <Typography variant="body2" color="text.secondary">{label}</Typography>
        <Typography variant="h6" fontWeight={800}>{value}</Typography>
      </Box>
    </Stack>
  </Card>
);

const InfoTile = ({ label, value, multiline = false }) => (
  <Card variant="outlined" sx={{ borderRadius: 2, height: "100%" }}>
    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
      <Typography variant="caption" sx={{ textTransform: "uppercase", letterSpacing: 0.6, color: "text.secondary" }}>
        {label}
      </Typography>
      <Typography variant="body1" sx={{ mt: 0.5, fontWeight: 600, whiteSpace: multiline ? "normal" : "nowrap", overflow: multiline ? "visible" : "hidden", textOverflow: multiline ? "clip" : "ellipsis" }}>
        {value}
      </Typography>
    </CardContent>
  </Card>
);
